// React imports
import { useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';

// General imports
import axios from 'axios';
import { useDropzone } from 'react-dropzone';

// MUI imports
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Card,
    CardContent,
} from '@mui/material';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp, faDownload, faFileCsv } from '@fortawesome/pro-light-svg-icons';

function CargofiveConverter() {
    const { accesstoken } = useOutletContext();
    const [file, setFile] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [csvData, setCsvData] = useState(null);
    const [error, setError] = useState(null);
    const [rowCount, setRowCount] = useState(0);

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length === 0) return;

        const selectedFile = acceptedFiles[0];
        if (!selectedFile.name.toLowerCase().endsWith('.json')) {
            setError('Please upload a JSON file');
            return;
        }

        setFile(selectedFile);
        setCsvData(null);
        setError(null);
        setRowCount(0);

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const jsonContent = JSON.parse(e.target.result);
                if (!Array.isArray(jsonContent)) {
                    setError('JSON file must contain an array of charge objects');
                    return;
                }

                setProcessing(true);
                const { data } = await axios.post(
                    `/api/cargofive/convert-to-csv?accesstoken=${accesstoken}`,
                    { charges: jsonContent }
                );

                setCsvData(data.csv);
                setRowCount(data.rowCount);
                setProcessing(false);
            } catch (err) {
                setError(err.response?.data?.detail || err.message || 'Failed to process file');
                setProcessing(false);
            }
        };
        reader.onerror = () => {
            setError('Failed to read file');
            setProcessing(false);
        };
        reader.readAsText(selectedFile);
    }, [accesstoken]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/json': ['.json'],
        },
        maxFiles: 1,
        disabled: processing,
    });

    const handleDownload = () => {
        if (!csvData) return;

        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', file ? file.name.replace('.json', '.csv') : 'cargofive-output.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box sx={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
            <Typography variant="h4" sx={{ marginBottom: '24px' }}>
                CargoFive JSON to CSV Converter
            </Typography>

            <Typography variant="body1" sx={{ marginBottom: '24px', color: 'text.secondary' }}>
                Upload a JSON file containing CargoFive charges to convert it to CSV format.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ marginBottom: '24px' }}>
                    {error}
                </Alert>
            )}

            <Card sx={{ marginBottom: '24px' }}>
                <CardContent>
                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDragActive ? 'primary.main' : 'grey.300',
                            borderRadius: '8px',
                            padding: '48px',
                            textAlign: 'center',
                            cursor: processing ? 'not-allowed' : 'pointer',
                            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
                            transition: 'all 0.2s ease',
                            opacity: processing ? 0.6 : 1,
                        }}
                    >
                        <input {...getInputProps()} />
                        {processing ? (
                            <Box>
                                <CircularProgress sx={{ marginBottom: '16px' }} />
                                <Typography>Processing file...</Typography>
                            </Box>
                        ) : (
                            <Box>
                                <FontAwesomeIcon
                                    icon={faCloudArrowUp}
                                    style={{ fontSize: '48px', color: '#1976d2', marginBottom: '16px' }}
                                />
                                <Typography variant="body1" sx={{ marginBottom: '8px' }}>
                                    {isDragActive
                                        ? 'Drop the JSON file here'
                                        : 'Drag and drop a JSON file here, or click to select'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Only JSON files are accepted
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {file && !processing && (
                        <Box sx={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Selected: {file.name}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {csvData && (
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                            <Box>
                                <Typography variant="h6">Conversion Complete</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {rowCount} row(s) generated
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<FontAwesomeIcon icon={faDownload} />}
                                onClick={handleDownload}
                            >
                                Download CSV
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}

export default CargofiveConverter;

